/**
 * Iter-246c — Admin Recap Compliance page.
 *
 * Lists every confirmed booking whose talent has not yet submitted a
 * post-event recap. Admins use this from the Operations Center to nudge
 * stale recaps and unblock payroll. Sorted by oldest first so the most
 * delinquent rises to the top.
 *
 * Lifecycle states surfaced (see backend `_compute_state`):
 *   • post_event_recap_required — clock-out done, awaiting recap
 *   • awaiting_admin_approval   — recap submitted, pending approval
 *
 * Mel feedback: this is a daily operational tool, not a configuration —
 * lives under Operations Center, not Settings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ArrowLeft, RefreshCw, MessageSquareQuote, Calendar, User, Briefcase, Clock, Loader2, Search, TrendingDown, TrendingUp, ClipboardCheck } from 'lucide-react';
import { formatDate } from '../../utils/timeFormat';
import { toast } from 'sonner';

const stateLabel = (s) => {
  if (s === 'awaiting_admin_approval') return { label: 'Awaiting Approval', cls: 'bg-amber-100 text-amber-700 border-amber-300' };
  if (s === 'post_event_recap_required') return { label: 'Recap Required', cls: 'bg-rose-100 text-rose-700 border-rose-300' };
  return { label: s || 'Unknown', cls: 'bg-slate-100 text-slate-700 border-slate-300' };
};

const pctBadge = (pct) => {
  if (pct >= 90) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
  if (pct >= 60) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-rose-100 text-rose-700 border-rose-300';
};

const AdminRecapCompliance = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2026-08-30 — per-talent completion analytics tile.
  const [summary, setSummary] = useState(null);
  const [talents, setTalents] = useState([]);
  const [talentsLoading, setTalentsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/post-event-recap/admin/pending-all?limit=200');
      setRows(r.data?.bookings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recap compliance');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTalentStats = useCallback(async (searchTerm) => {
    setTalentsLoading(true);
    try {
      const params = new URLSearchParams({ days: '60', min_gigs: '1' });
      if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
      const r = await api.get(`/admin/recap-compliance?${params}`);
      setSummary(r.data?.summary || null);
      setTalents(r.data?.talents || []);
    } catch (e) {
      // Non-fatal — pending list still works.
      setSummary(null);
      setTalents([]);
    } finally {
      setTalentsLoading(false);
    }
  }, []);

  useEffect(() => { loadPending(); loadTalentStats(''); }, [loadPending, loadTalentStats]);

  const onSearch = (e) => {
    e.preventDefault();
    loadTalentStats(search);
  };

  const daysAgo = (iso) => {
    if (!iso) return null;
    const d = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.floor(d));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="recap-compliance-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquareQuote className="w-6 h-6 text-cyan-600" />
            Post-Event Recap Compliance
          </h1>
          <Button
            variant="outline" size="sm" className="ml-auto"
            onClick={() => { loadPending(); loadTalentStats(search); }}
            data-testid="recap-compliance-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${(loading || talentsLoading) ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary tile — 60-day window per-talent completion. */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="recap-compliance-summary">
            <Card>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <ClipboardCheck className="w-3.5 h-3.5" /> Talent tracked
                </div>
                <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="recap-summary-talent-count">
                  {summary.talent_count}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">last {summary.window_days}d</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Avg completion</div>
                <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="recap-summary-avg-pct">
                  {summary.avg_completion_pct}%
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> Compliant (≥90%)
                </div>
                <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="recap-summary-compliant-pct">
                  {summary.compliant_pct}%
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <TrendingDown className="w-3.5 h-3.5 text-rose-600" /> At risk (&lt;60%)
                </div>
                <div className="text-2xl font-bold text-rose-700 mt-1" data-testid="recap-summary-nonc-pct">
                  {summary.non_compliant_pct}%
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{summary.total_pending} recaps pending</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Per-talent completion table. */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm">Per-talent completion (last 60 days)</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Sorted by lowest completion first — nudge the top of the list.
              </p>
            </div>
            <form onSubmit={onSearch} className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or email"
                  className="h-8 pl-7 text-xs w-56"
                  data-testid="recap-compliance-search-input"
                />
              </div>
              <Button type="submit" variant="outline" size="sm" data-testid="recap-compliance-search-btn">Search</Button>
            </form>
          </CardHeader>
          <CardContent className="p-0">
            {talentsLoading ? (
              <div className="p-10 grid place-items-center">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : talents.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500" data-testid="recap-talents-empty">
                No talent match the current filter.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {talents.map((t) => (
                  <div
                    key={t.talent_id}
                    className="p-3 sm:p-4 flex items-center gap-3 flex-wrap hover:bg-slate-50 transition"
                    data-testid={`recap-talent-row-${t.talent_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{t.name}</div>
                      <div className="text-xs text-slate-500 truncate">{t.email}</div>
                    </div>
                    <div className="text-xs text-slate-600 min-w-[140px]">
                      {t.completed_recaps}/{t.eligible_gigs} recaps
                      {t.pending_recaps > 0 && (
                        <span className="text-rose-600 ml-1">· {t.pending_recaps} pending</span>
                      )}
                    </div>
                    <Badge className={`text-xs border ${pctBadge(t.completion_pct)}`} data-testid={`recap-talent-pct-${t.talent_id}`}>
                      {t.completion_pct}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Existing pending-bookings list. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              Pending recaps
              <Badge variant="outline" className="ml-2" data-testid="recap-compliance-count">{rows.length}</Badge>
            </CardTitle>
            <p className="text-xs text-slate-500">
              Oldest first. Bookings disappear once the talent submits a recap and the client
              rating is captured. Use the booking link to open the shift detail and prompt the
              talent (DM, SMS, email).
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 grid place-items-center">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500" data-testid="recap-compliance-empty">
                All caught up — every confirmed booking has a recap on file.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {rows.map((b) => {
                  const days = daysAgo(b.clock_out_time || b.shift_end);
                  const meta = stateLabel(b.lifecycle_state);
                  return (
                    <div
                      key={b.booking_id}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition"
                      onClick={() => navigate(`/admin/shifts/${b.shift_id}`)}
                      data-testid={`recap-row-${b.booking_id}`}
                    >
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm truncate" data-testid="recap-talent-name">
                              <User className="w-3.5 h-3.5 inline-block mr-1 text-slate-400" />
                              {b.talent_name}
                            </span>
                            <Badge className={`text-[10px] border ${meta.cls}`}>{meta.label}</Badge>
                            {days != null && days >= 7 && (
                              <Badge className="text-[10px] bg-red-100 text-red-700 border-red-300">
                                {days}d stale
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-slate-600 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                              {b.shift_title || 'Shift'}
                              {b.client_name && <span className="text-slate-400">· {b.client_name}</span>}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              {b.shift_start ? formatDate(b.shift_start) : '—'}
                            </span>
                            {b.clock_out_time && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                Clocked out {formatDate(b.clock_out_time)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminRecapCompliance;
